import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import React, { MouseEventHandler } from 'react'
import { getButtonTestId } from '../Buttons/getButtonTestId'

/** @notExported */
interface BadgeIconButtonProps {
  /** Action to perform when button is clicked. */
  clickAction?: MouseEventHandler<unknown>
  /** Tooltip for button. */
  tooltip?: string | React.ReactNode
  /** Whether or not the button is valid. */
  valid?: boolean
  /** Size of the button. */
  size?: 'medium' | 'small' | undefined | 'xsmall'
  /** Icon to display. */
  icon: React.ReactNode
  /** Badge content. */
  badgeContent: number | string
  /** Test id of the button. */
  testId?: string
  /** Accessible label for screen readers when tooltip is not a plain string. */
  ariaLabel?: string
}

/**
 * Badge icon button component.
 *
 * @returns Badge icon button component.
 * @notExported
 * @category Buttons
 */
const BadgeIconButton: React.FC<BadgeIconButtonProps> = ({
  clickAction,
  tooltip,
  valid = true,
  size = 'small',
  icon,
  badgeContent,
  testId,
  ariaLabel,
}) => {
  const label = typeof tooltip === 'string' ? tooltip : (ariaLabel ?? 'badge button')
  const resolvedTestId = getButtonTestId({ fallback: 'button-badge-icon', label, testId })

  const getButton = () => {
    return (
      <IconButton
        aria-label={label}
        data-testid={resolvedTestId}
        sx={{ float: 'right' }}
        onClick={clickAction}
        disabled={!valid}
        size={size === 'xsmall' ? 'medium' : size}
      >
        <Badge badgeContent={badgeContent} color="primary">
          {icon}
        </Badge>
      </IconButton>
    )
  }
  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} describeChild>
          {getButton()}
        </Tooltip>
      ) : (
        getButton()
      )}
    </>
  )
}

export default BadgeIconButton
